import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { handleApiResponse } from "./../helpers/FormValidation";
import { showSuccessMessage, showErrorMessage } from "./../utils/alertHelper";
import moment from "moment"; // Add moment for date handling
import TopNavbar from "../components/TopNavbar";

import { Container, Dropdown } from "react-bootstrap";

const PosInvoice = () => {
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  // Theme Management
  useEffect(() => {
    document.body.className = theme;
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };
  const [user, setUser] = useState(null);

  // Initialize user data from localStorage
  const login_user = JSON.parse(localStorage.getItem("login_user") || "{}");
  const [item, setItem] = useState({
    customer_id: "",
    invoice_date: moment().format("YYYY-MM-DD"), // Set current date as default
    due_date: moment().add(7, "days").format("YYYY-MM-DD"), // Set due date 7 days from now
    notes: "",
    location_id: login_user?.location_id || "",
    device_id: login_user?.device_id || "",
    user_id: login_user?.id || "",
  });

  const [taxConfig, setTaxConfig] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalShow, setModalShow] = useState(false);

  const navigate = useNavigate();
  const baseURL = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("token");

  // Fetch customers
  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${baseURL}/customers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomers(response.data.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
      showErrorMessage("Failed to fetch customer details.");
    }
  };

  // Fetch invoices
  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseURL}/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(response.data.data || []);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      showErrorMessage("Failed to fetch invoices.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch tax configurations
  const fetchTaxConfig = async () => {
    try {
      const response = await axios.get(`${baseURL}/taxconfig`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTaxConfig(response.data.data || []);
    } catch (error) {
      console.error("Error fetching tax configurations:", error);
    }
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setItem((prev) => ({ ...prev, [name]: value }));

    // Auto-update due date when invoice date changes
    if (name === "invoice_date" && value) {
      const dueDate = moment(value).add(7, "days").format("YYYY-MM-DD");
      setItem((prev) => ({ ...prev, due_date: dueDate }));
    }
  };

  // Submit new invoice
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${baseURL}/invoices/add-new-invoice`,
        item,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      showSuccessMessage("Invoice added successfully!");
      fetchInvoices(); // Refresh the invoice list
      resetForm();
      setModalShow(false);
    } catch (error) {
      handleApiResponse(
        error.response?.data,
        "An error occurred during submission",
        null,
        navigate
      );
    }
  };

  // Reset form
  const resetForm = () => {
    setItem({
      customer_id: "",
      invoice_date: moment().format("YYYY-MM-DD"),
      due_date: moment().add(7, "days").format("YYYY-MM-DD"),
      notes: "",
      location_id: login_user?.location_id || "",
      device_id: login_user?.device_id || "",
      user_id: login_user?.id || "",
    });
  };

  // Delete Invoice
  const handleDeleteInvoice = async (invoice_id) => {
    if (!window.confirm("Are you sure you want to delete this invoice?"))
      return;
    try {
      await axios.delete(`${baseURL}/invoices/deleteInvoice/${invoice_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(
        invoices.filter((invoice) => invoice.invoice_id !== invoice_id)
      );
      showSuccessMessage("Invoice deleted successfully!");
    } catch (error) {
      console.error("Error deleting invoice:", error);
      showErrorMessage("Failed to delete invoice.");
    }
  };

  useEffect(() => {
    fetchTaxConfig();
    fetchCustomers();
    fetchInvoices();
  }, []);

  return (
    <div className={`dashboard ${theme}`}>
      <Container
        fluid
        className={`vh-100 ${
          theme === "dark" ? "bg-dark text-white" : "bg-light text-dark"
        }`}
      >
        <TopNavbar user={user} toggleTheme={toggleTheme} theme={theme} />
        <div className="page-wrapper">
          <div className={`page-content-wrapper ${
          theme === "dark" ? "bg-dark text-white" : "bg-light text-dark"
        }`}>
            <div className="page-content">
              <div className="page-breadcrumb d-none d-sm-flex align-items-center mb-3">
                <div className="breadcrumb-title pe-3">Home</div>
                <div className="ps-3">
                  <nav aria-label="breadcrumb">
                    <ol className="breadcrumb mb-0 p-0">
                      <li className="breadcrumb-item">
                        <Link to="/">
                          <i className="bx bx-home-alt"></i>
                        </Link>
                      </li>
                      <li
                        className="breadcrumb-item active"
                        aria-current="page"
                      >
                        POS Invoice
                      </li>
                    </ol>
                  </nav>
                </div>
                <div className="ms-auto">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setModalShow(true)}
                  >
                    <i className="bx bx-plus me-1"></i> Add POS Invoice
                  </button>
                </div>
              </div>

              <div className="row">
                <div className="col-xl-12 mx-auto">
                  <div className={`card ${
          theme === "dark" ? "bg-dark text-white" : "bg-light text-dark"
        }`}>
                    <div className="card-body">
                      {loading ? (
                        <div className="text-center py-4">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="table-responsive ">
                            <table
                            className={`table table-striped table-bordered ${
                                theme === "dark" ? "table-dark" : "table-light"
                              }`}
                              width="100%"
                            >
                              <thead>
                                <tr>
                                  <th>#</th>
                                  <th>Ref</th>
                                  <th>Customer</th>
                                  <th>User</th>
                                  <th>Invoice Date</th>
                                  <th>Due Date</th>
                                  <th>Total</th>
                                  <th>Balance</th>
                                  <th>Status</th>
                                  <th>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {invoices.map((invoice) => (
                                  <tr key={invoice.invoice_id}>
                                    <td>{invoice.invoice_id}</td>
                                    <td>{invoice.ref}</td>
                                    <td>
                                      {invoice.customer_name ||
                                        "Walk-in Customer"}
                                    </td>
                                    <td>{invoice.user_name}</td>
                                    <td>
                                      {moment(invoice.invoice_date).format(
                                        "DD/MM/YYYY"
                                      )}
                                    </td>
                                    <td>
                                      {moment(invoice.due_date).format(
                                        "DD/MM/YYYY"
                                      )}
                                    </td>
                                    <td>
                                      $
                                      {parseFloat(invoice.total || 0).toFixed(
                                        2
                                      )}
                                    </td>
                                    <td>
                                      $
                                      {parseFloat(invoice.balance || 0).toFixed(
                                        2
                                      )}
                                    </td>
                                    <td>
                                      <span
                                        className={`badge rounded-pill ${
                                          invoice.invoice_status === "paid"
                                            ? "bg-success"
                                            : "bg-warning"
                                        }`}
                                      >
                                        {invoice.invoice_status}
                                      </span>
                                    </td>
                                    <td>
                                      <td>
                                        <Dropdown >
                                          <Dropdown.Toggle
                                            variant="primary"
                                            size="sm"
                                            id="dropdown-basic"
                                          >
                                            Actions
                                          </Dropdown.Toggle>

                                          <Dropdown.Menu>
                                            <Dropdown.Item
                                              as={Link}
                                              to={`/invoice/${invoice.invoice_id}`}
                                            >
                                              View Details
                                            </Dropdown.Item>
                                            <Dropdown.Item
                                              as={Link}
                                              to={`/update-pos-invoice/${invoice.invoice_id}`}
                                            >
                                              Update
                                            </Dropdown.Item>
                                            <Dropdown.Item
                                              className="text-danger"
                                              onClick={() =>
                                                handleDeleteInvoice(
                                                  invoice.invoice_id
                                                )
                                              }
                                            >
                                              Delete
                                            </Dropdown.Item>
                                          </Dropdown.Menu>
                                        </Dropdown>
                                      </td>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Add Invoice Modal */}
              <div
                className={`modal fade ${modalShow ? "show d-block" : ""}`}
                id="addItem"
                tabIndex="-1"
              >
                <div className="modal-dialog modal-lg">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Add New Invoice</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setModalShow(false)}
                      ></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                      <div className="modal-body">
                        <input
                          type="hidden"
                          name="location_id"
                          value={item.location_id}
                        />
                        <input
                          type="hidden"
                          name="device_id"
                          value={item.device_id}
                        />
                        <input
                          type="hidden"
                          name="user_id"
                          value={item.user_id}
                        />

                        <div className="row mb-3">
                          <div className="col-md-12">
                            <label className="form-label">Customer</label>
                            <select
                              className="form-select"
                              name="customer_id"
                              value={item.customer_id}
                              onChange={handleChange}
                              required
                            >
                              <option value="">Select Customer</option>
                              {customers.map((customer) => (
                                <option key={customer.id} value={customer.id}>
                                  {customer.name} - {customer.phone}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="form-label">Invoice Date</label>
                            <input
                              type="date"
                              className="form-control"
                              name="invoice_date"
                              value={item.invoice_date}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Due Date</label>
                            <input
                              type="date"
                              className="form-control"
                              name="due_date"
                              value={item.due_date}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </div>

                        <div className="row mb-3">
                          <div className="col-md-12">
                            <label className="form-label">Notes</label>
                            <textarea
                              className="form-control"
                              name="notes"
                              value={item.notes}
                              onChange={handleChange}
                              rows="3"
                            ></textarea>
                          </div>
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setModalShow(false)}
                        >
                          Cancel
                        </button>
                        <button type="submit" className="btn btn-primary">
                          Save Invoice
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
              {modalShow && <div className="modal-backdrop fade show"></div>}
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default PosInvoice;
